import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Calendar, Star, Plus, X, Link2, Users, CornerDownRight } from "lucide-react";
import { format, isPast, endOfDay } from "date-fns";
import { formatDateInput } from "@/lib/dateUtils";
import type { SubTask, FriendWithUser } from "./task-types";

/**
 * Milestone-scoped sub-tasks: rendered under each milestone in the Edit Task
 * dialog. Each sub-task supports a deadline, starring, assignment
 * (Me / Friend-assistant / Other person), an optional dependency on a sibling
 * sub-task, and one level of nested sub-sub-tasks with the same controls.
 */

export interface MilestoneSubTaskHandlers {
  onCreate: (fields: Partial<SubTask> & { text: string }) => void;
  onUpdate: (id: string, updates: Partial<SubTask>) => void;
  onToggle: (id: string) => void;
  onStar: (id: string) => void;
  onDelete: (id: string) => void;
  onDateChange: (id: string, date: string | null) => void;
}

function AssignPopover({
  subTask,
  onUpdate,
  readOnly,
}: {
  subTask: SubTask;
  onUpdate: (id: string, updates: Partial<SubTask>) => void;
  readOnly?: boolean;
}) {
  const [open, setOpen] = useState(false);

  // Draft state tracks in-popover edits independently of the subTask prop.
  // This is the key invariant: the guard on close always reads the *current*
  // selection made by the user, not a potentially stale prop that hasn't
  // re-rendered from a mutation yet. No onUpdate fires during editing —
  // persistence is deferred to the single close-time call below.
  const [draftAssignedTo, setDraftAssignedTo] = useState<"Me" | "Friend" | "Other">(
    () => (subTask.assignedTo as "Me" | "Friend" | "Other" | undefined) ?? "Me",
  );
  const [draftUserId, setDraftUserId] = useState<string | null>(subTask.assignedToUserId ?? null);
  const [draftOther, setDraftOther] = useState<string>(subTask.assignedToOther ?? "");

  const { data: friends = [] } = useQuery<FriendWithUser[]>({
    queryKey: ["/api/friends"],
    enabled: open,
  });

  // Trigger button label always reflects the committed (prop) state so it stays
  // in sync with what's actually saved between popover sessions.
  const committedAssignedTo = subTask.assignedTo ?? "Me";
  const assignedLabel =
    committedAssignedTo === "Friend"
      ? friends.find((f) => f.friend?.id === subTask.assignedToUserId)?.friend?.firstName ?? "Assigned"
      : committedAssignedTo === "Other"
        ? subTask.assignedToOther || "Other"
        : null;

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          // Sync draft from the committed prop each time the popover opens so
          // any server-side update that happened while it was closed is picked up.
          setDraftAssignedTo((subTask.assignedTo as "Me" | "Friend" | "Other" | undefined) ?? "Me");
          setDraftUserId(subTask.assignedToUserId ?? null);
          setDraftOther(subTask.assignedToOther ?? "");
        } else {
          // Guard: apply fall-back-to-Me if the draft is incomplete, then
          // persist the resolved assignment in a single onUpdate call.
          // Mirror the resolveTaskAssignment rule from task-form-shared so
          // sub-tasks obey the same invariant.
          if (draftAssignedTo === "Friend" && !draftUserId) {
            onUpdate(subTask.id, { assignedTo: "Me", assignedToUserId: null, assignedToOther: null });
          } else if (draftAssignedTo === "Other" && !draftOther.trim()) {
            onUpdate(subTask.id, { assignedTo: "Me", assignedToUserId: null, assignedToOther: null });
          } else {
            // Only persist if the draft actually differs from the committed state.
            const resolvedUserId = draftAssignedTo === "Friend" ? draftUserId : null;
            const resolvedOther = draftAssignedTo === "Other" ? draftOther.trim() || null : null;
            if (
              draftAssignedTo !== (subTask.assignedTo ?? "Me") ||
              resolvedUserId !== (subTask.assignedToUserId ?? null) ||
              resolvedOther !== (subTask.assignedToOther ?? null)
            ) {
              onUpdate(subTask.id, {
                assignedTo: draftAssignedTo,
                assignedToUserId: resolvedUserId,
                assignedToOther: resolvedOther,
              });
            }
          }
        }
        setOpen(nextOpen);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={readOnly}
          className={`h-7 px-1.5 text-xs ${committedAssignedTo !== "Me" ? "text-primary" : "text-muted-foreground"}`}
          title={assignedLabel ? `Assigned to ${assignedLabel}` : "Assign"}
          data-testid={`button-assign-subtask-${subTask.id}`}
        >
          <Users className="h-3.5 w-3.5" />
          {assignedLabel ? <span className="ml-1 max-w-[80px] truncate">{assignedLabel}</span> : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 space-y-2" align="end">
        <Label className="text-xs">Assign To</Label>
        <Select
          value={draftAssignedTo}
          onValueChange={(v) => {
            const type = v as "Me" | "Friend" | "Other";
            setDraftAssignedTo(type);
            if (type === "Me") {
              setDraftUserId(null);
              setDraftOther("");
            } else if (type === "Friend") {
              setDraftOther("");
            } else {
              setDraftUserId(null);
            }
          }}
        >
          <SelectTrigger className="h-8" data-testid={`select-assign-subtask-${subTask.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Me">Me</SelectItem>
            <SelectItem value="Friend">Friend / Assistant</SelectItem>
            <SelectItem value="Other">Other Person</SelectItem>
          </SelectContent>
        </Select>
        {draftAssignedTo === "Friend" && (
          <Select
            value={draftUserId ?? ""}
            onValueChange={(userId) => setDraftUserId(userId)}
          >
            <SelectTrigger className="h-8" data-testid={`select-assign-friend-subtask-${subTask.id}`}>
              <SelectValue placeholder="Pick a person" />
            </SelectTrigger>
            <SelectContent>
              {friends
                .filter((f) => f.friend?.id)
                .map((f) => (
                  <SelectItem key={f.friend.id} value={f.friend.id}>
                    {[f.friend.firstName, f.friend.lastName].filter(Boolean).join(" ") || f.friend.email || f.friend.username || "Unnamed"}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        )}
        {draftAssignedTo === "Other" && (
          <Input
            className="h-8"
            value={draftOther}
            placeholder="Person's name"
            onChange={(e) => setDraftOther(e.target.value)}
            onBlur={(e) => setDraftOther(e.target.value.trim())}
            data-testid={`input-assign-other-subtask-${subTask.id}`}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

function DependencyPopover({
  subTask,
  siblings,
  onUpdate,
  readOnly,
}: {
  subTask: SubTask;
  siblings: SubTask[];
  onUpdate: (id: string, updates: Partial<SubTask>) => void;
  readOnly?: boolean;
}) {
  const options = siblings.filter((s) => s.id !== subTask.id);
  const dependsOn = options.find((s) => s.id === subTask.dependsOnSubTaskId);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={readOnly}
          className={`h-7 px-1.5 text-xs ${dependsOn ? "text-primary" : "text-muted-foreground"}`}
          title={dependsOn ? `Depends on: ${dependsOn.text}` : "Standalone — set a dependency"}
          data-testid={`button-dependency-subtask-${subTask.id}`}
        >
          <Link2 className="h-3.5 w-3.5" />
          {dependsOn ? <span className="ml-1 max-w-[80px] truncate">{dependsOn.text}</span> : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 space-y-2" align="end">
        <Label className="text-xs">Dependency</Label>
        <p className="text-xs text-muted-foreground">
          Standalone, or blocked by another sub-task at the same level.
        </p>
        <Select
          value={subTask.dependsOnSubTaskId ?? "none"}
          onValueChange={(v) =>
            onUpdate(subTask.id, { dependsOnSubTaskId: v === "none" ? null : v })
          }
        >
          <SelectTrigger className="h-8" data-testid={`select-dependency-subtask-${subTask.id}`}>
            <SelectValue placeholder="Standalone" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Standalone (no dependency)</SelectItem>
            {options.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.text}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PopoverContent>
    </Popover>
  );
}

function SubTaskRow({
  subTask,
  siblings,
  handlers,
  readOnly,
  depth,
  children,
}: {
  subTask: SubTask;
  siblings: SubTask[];
  handlers: MilestoneSubTaskHandlers;
  readOnly?: boolean;
  depth: number;
  children?: React.ReactNode;
}) {
  const dependsOn = siblings.find((s) => s.id === subTask.dependsOnSubTaskId);
  const blocked = !!dependsOn && !dependsOn.completed;
  const overdue = subTask.dueDate && !subTask.completed && isPast(endOfDay(new Date(subTask.dueDate)));

  return (
    <div className={depth > 0 ? "ml-6" : undefined}>
      <div
        className="flex items-center gap-1.5 rounded border bg-card px-2 py-1.5 flex-wrap"
        data-testid={`milestone-subtask-item-${subTask.id}`}
      >
        {depth > 0 && <CornerDownRight className="h-3 w-3 text-muted-foreground shrink-0" />}
        <input
          type="checkbox"
          checked={subTask.completed}
          disabled={readOnly}
          onChange={() => { if (!readOnly) handlers.onToggle(subTask.id); }}
          className="h-4 w-4 shrink-0"
          data-testid={`checkbox-milestone-subtask-${subTask.id}`}
        />
        <Input
          defaultValue={subTask.text}
          readOnly={readOnly}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (!readOnly && v && v !== subTask.text) handlers.onUpdate(subTask.id, { text: v });
          }}
          className={`h-7 flex-1 min-w-[120px] border-0 bg-transparent px-1 text-sm focus-visible:ring-1 ${subTask.completed ? "line-through text-muted-foreground" : ""}`}
          data-testid={`input-milestone-subtask-text-${subTask.id}`}
        />
        {blocked && (
          <span className="text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400" data-testid={`badge-blocked-subtask-${subTask.id}`}>
            blocked
          </span>
        )}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={readOnly}
              className={`h-7 px-1.5 text-xs ${overdue ? "text-destructive" : subTask.dueDate ? "text-primary" : "text-muted-foreground"}`}
              title="Deadline"
              data-testid={`button-milestone-subtask-date-${subTask.id}`}
            >
              <Calendar className="h-3.5 w-3.5" />
              {subTask.dueDate ? <span className="ml-1">{format(new Date(subTask.dueDate), "MMM d")}</span> : null}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <CalendarPicker
              mode="single"
              selected={subTask.dueDate ? new Date(subTask.dueDate) : undefined}
              onSelect={(date) => handlers.onDateChange(subTask.id, date ? formatDateInput(date) : null)}
            />
            {subTask.dueDate && (
              <div className="border-t p-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs"
                  onClick={() => handlers.onDateChange(subTask.id, null)}
                >
                  Clear date
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
        <DependencyPopover subTask={subTask} siblings={siblings} onUpdate={handlers.onUpdate} readOnly={readOnly} />
        <AssignPopover subTask={subTask} onUpdate={handlers.onUpdate} readOnly={readOnly} />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={readOnly}
          className="h-7 w-7 p-0"
          onClick={() => handlers.onStar(subTask.id)}
          title={subTask.isStarred ? "Unstar" : "Star"}
          data-testid={`button-star-milestone-subtask-${subTask.id}`}
        >
          <Star className={`h-3.5 w-3.5 ${subTask.isStarred ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"}`} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={readOnly}
          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
          onClick={() => handlers.onDelete(subTask.id)}
          data-testid={`button-delete-milestone-subtask-${subTask.id}`}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      {children}
    </div>
  );
}

function AddSubTaskInput({
  placeholder,
  onAdd,
  testId,
  isPending,
}: {
  placeholder: string;
  onAdd: (text: string, dueDate: string | null) => void;
  testId: string;
  isPending?: boolean;
}) {
  const [text, setText] = useState("");
  const [dueDate, setDueDate] = useState("");
  const submit = () => {
    const v = text.trim();
    if (!v || isPending) return;
    onAdd(v, dueDate || null);
    setText("");
    setDueDate("");
  };
  return (
    <div className="flex gap-1.5">
      <Input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        }}
        placeholder={placeholder}
        className="h-7 text-sm"
        data-testid={testId}
      />
      <Input
        type="date"
        value={dueDate}
        onChange={(e) => setDueDate(e.target.value)}
        className="h-7 w-36 text-sm"
        title="Due date (optional)"
        data-testid={`${testId}-date`}
      />
      <Button type="button" variant="outline" size="sm" className="h-7 px-2" onClick={submit} disabled={!text.trim() || isPending}>
        <Plus className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export default function MilestoneSubTasks({
  milestoneId,
  allSubTasks,
  handlers,
  readOnly,
  showAddInput = true,
  isCreating,
}: {
  milestoneId: string;
  allSubTasks: SubTask[];
  handlers: MilestoneSubTaskHandlers;
  readOnly?: boolean;
  /** When false, the "Add sub-task to this milestone" input is hidden until requested (e.g. via a + icon). */
  showAddInput?: boolean;
  /** When true, the Add buttons are disabled to prevent double-submit while a create is in flight. */
  isCreating?: boolean;
}) {
  const milestoneSubTasks = allSubTasks.filter((st) => st.milestoneId === milestoneId);
  const topLevel = milestoneSubTasks
    .filter((st) => !st.parentSubTaskId)
    .sort((a, b) => a.displayOrder - b.displayOrder);
  const childrenOf = (parentId: string) =>
    milestoneSubTasks
      .filter((st) => st.parentSubTaskId === parentId)
      .sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <div className="ml-6 mt-1 space-y-1.5" data-testid={`milestone-subtasks-${milestoneId}`}>
      {topLevel.map((st) => {
        const kids = childrenOf(st.id);
        return (
          <SubTaskRow key={st.id} subTask={st} siblings={topLevel} handlers={handlers} readOnly={readOnly} depth={0}>
            <div className="space-y-1.5 mt-1.5">
              {kids.map((kid) => (
                <SubTaskRow key={kid.id} subTask={kid} siblings={kids} handlers={handlers} readOnly={readOnly} depth={1} />
              ))}
              {!readOnly && (
                <div className="ml-6">
                  <AddSubTaskInput
                    placeholder="Add sub-sub-task..."
                    onAdd={(text, dueDate) => handlers.onCreate({ text, milestoneId, parentSubTaskId: st.id, dueDate })}
                    testId={`input-new-subsubtask-${st.id}`}
                    isPending={isCreating}
                  />
                </div>
              )}
            </div>
          </SubTaskRow>
        );
      })}
      {!readOnly && showAddInput && (
        <AddSubTaskInput
          placeholder="Add sub-task to this milestone..."
          onAdd={(text, dueDate) => handlers.onCreate({ text, milestoneId, dueDate })}
          testId={`input-new-milestone-subtask-${milestoneId}`}
          isPending={isCreating}
        />
      )}
    </div>
  );
}
